import PropTypes from 'prop-types';
import { Badge, ListItem, Tooltip, Button, Collapse, Box } from '@mui/material';
import { type NavBarItem, type NavBarChildItem } from './NavbarContent';
import clsx from 'clsx';
import { useStylesForNavItem } from './styles';
import { NavLink as RouterLink } from 'react-router-dom';
import { selectMenuOpened } from '../../../../old/actions/menu/menuSelectors';
import { useSelector } from 'react-redux';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import useLogout from 'src/shared/hooks/useLogout';

type NavItemsProps = {
  item: NavBarItem | NavBarChildItem;
  isChild?: boolean;
  index: number;
  expandedIndex: number | null;
  setExpandedIndex: (index: number | null) => void;
};

const NavItems = ({ item, isChild, index, expandedIndex, setExpandedIndex }: NavItemsProps) => {
  const menuOpen = useSelector(selectMenuOpened);
  const { logoutUser, isLoggingOut } = useLogout();
  const collapsed = !menuOpen;
  const classes = useStylesForNavItem({ collapsed });
  const hasChildren = 'items' in item && item.items && item.items.length > 0;
  const isLogoutItem = !item.isExternal && item.action === 'logout';
  const hasHref = item.href != null;
  const isNavigableInternalLink = !item.isExternal && !isLogoutItem && hasHref;
  // Build a unique path for each item in the tree
  const isExpanded = expandedIndex === index;

  const handleClick = () => {
    if (hasChildren && setExpandedIndex) {
      if (isExpanded) {
        setExpandedIndex(null);
      } else {
        setExpandedIndex(index);
      }
    }
  };

  const handleLogoutClick = () => {
    if (isLoggingOut) {
      return;
    }
    void logoutUser();
  };

  const renderButton = () => {
    if (hasChildren) {
      return (
        <Button
          className={clsx({
            [classes.buttonLeaf]: true,
            [classes.alignCenter]: collapsed
          })}
          onClick={handleClick}
          fullWidth
        >
          {item.icon && (
            <Tooltip title={item.title} placement="right">
              <item.icon className={classes.icon} />
            </Tooltip>
          )}

          <Badge
            classes={{ badge: classes.badge }}
            className={classes.item}
            badgeContent={item?.badge}
            max={99}
            color="error"
          >
            {menuOpen ? item.title : ''}
            {/* Arrow for items with children */}
            {isExpanded ? <ExpandLess sx={{ mt: '3px' }} /> : <ExpandMore sx={{ mt: '3px' }} />}
          </Badge>
        </Button>
      );
    } else {
      return (
        <Button
          className={clsx({
            [classes.buttonLeaf]: true,
            [classes.alignCenter]: collapsed
          })}
          activeClassName={classes.active}
          component={item.isExternal ? 'a' : isNavigableInternalLink ? RouterLink : 'button'}
          to={isNavigableInternalLink ? { pathname: item.href } : undefined}
          href={item.isExternal ? item.href : undefined}
          target={item.isExternal ? '_blank' : undefined}
          onClick={isLogoutItem ? handleLogoutClick : undefined}
        >
          {item.icon && (
            <Tooltip title={item.title} placement="right">
              <item.icon className={classes.icon} />
            </Tooltip>
          )}
          <Badge
            classes={{ badge: classes.badge }}
            className={classes.item}
            badgeContent={item?.badge}
            max={99}
            color="error"
          >
            {menuOpen ? item.title : ''}
          </Badge>
        </Button>
      );
    }
  };

  return (
    <Box mt={isChild ? 1 : 4} mb={isChild ? 0 : 3}>
      <ListItem
        className={clsx({
          [classes.itemLeaf]: true,
          [classes.alignCenter]: collapsed
        })}
        disableGutters
      >
        {renderButton()}
      </ListItem>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {item.items?.map((childItem, childIdx) => (
            <div key={childIdx}>
              <NavItems
                item={childItem}
                isChild
                index={childIdx}
                expandedIndex={expandedIndex}
                setExpandedIndex={setExpandedIndex}
              />
            </div>
          ))}
        </Collapse>
      )}
    </Box>
  );
};

export default NavItems;
